/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, View, Platform, FlatListProps } from 'react-native';
import { useWindowDimensions } from 'react-native';
import { getTextStyle, pd } from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet } from 'react-native-unistyles';
import HorizontalContentCardGroupView from '@airasia-phoenix/react-native/widgets/HorizontalContentCardGroupView';
import { HorizontalContentCardProps } from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  useAppConfig,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { Constants } from '../HotelDealsCarouselView/constants';

import { useLogger } from '@airasia-rn/core-logger';
import { TRACKING_CONSTANTS } from '../../configs/constants';
import { useHotelConfig } from '../../configs/HotelConfigProvider';
import { useContinueSearches } from '../../hooks/useContinueSearch';
import { useHandleHotelListNavigation } from '../../hooks/useHandleHotelListNavigation';
import { useIsClientHydrated } from '../../hooks/useIsClientHydrated';
import {
  buildHotelListUrlFromRecentSearch,
  buildNewWebHotelListUrlFromRecentSearch,
} from '../../utils/deeplink';
import { getRecentSearchContentCards } from './utils';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';
import type { HotelRecentSearch } from '../../types';

interface RecentSearchViewProps {
  showTitle?: boolean;
  testID?: string;
  onNavigate?: () => void;
}

const RecentSearchView: React.FC<RecentSearchViewProps> = ({
  showTitle = true,
  testID = 'recent-search-view',
  onNavigate,
}) => {
  const { t } = useTranslation('hotels');
  const { logEvent } = useLogger();
  const { width: screenWidth } = useWindowDimensions();
  const hasTrackedRef = useRef(false);
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { config: hotelConfig } = useHotelConfig();
  const {
    navigateToHotelListUrl,
    getHotelListRoutePath,
    shouldUseNewWebListing,
  } = useHandleHotelListNavigation();
  const { geoId: countryCodeFromContext } = useSuperAppContext();
  const { EVENT_NAMES } = SearchWidgetMeta;

  const { continueSearches: rawContinueSearches, isLoading } =
    useContinueSearches();
  const isClientHydrated = useIsClientHydrated();
  const continueSearches = isClientHydrated ? rawContinueSearches : [];

  const handleRecentSearchPress = useCallback(
    (index: number) => {
      const selectedRecentSearch = (continueSearches as HotelRecentSearch[])[
        index
      ] as HotelRecentSearch;

      if (!selectedRecentSearch) {
        return;
      }

      // Fire last searched clicked event
      const analyticsParams = SearchWidgetEvent.lastSearchedClickedEvent({
        destinationId: selectedRecentSearch.destination?.id || '',
        position: index,
      });
      logEvent(EVENT_NAMES.LAST_SEARCHED_CLICKED, analyticsParams);

      const buildListUrl = shouldUseNewWebListing
        ? buildNewWebHotelListUrlFromRecentSearch
        : buildHotelListUrlFromRecentSearch;
      const listPageUrl = buildListUrl({
        routePath: getHotelListRoutePath(),
        recentSearch: selectedRecentSearch,
        currency: currency || DEFAULT_CURRENCY,
        countryCode:
          hotelConfig.geoId ??
          appConfig.geoId ??
          countryCodeFromContext ??
          'MY',
        languageId: appConfig.locale || DEFAULT_LANGUAGE,
      });

      onNavigate?.();
      navigateToHotelListUrl(listPageUrl);
    },
    [
      continueSearches,
      appConfig,
      currency,
      hotelConfig.geoId,
      countryCodeFromContext,
      navigateToHotelListUrl,
      getHotelListRoutePath,
      shouldUseNewWebListing,
      logEvent,
      EVENT_NAMES.LAST_SEARCHED_CLICKED,
      onNavigate,
    ],
  );

  // Memoize content cards to prevent unnecessary re-renders
  const contentCardProps = useMemo(() => {
    if (!continueSearches || continueSearches.length === 0) return [];
    return getRecentSearchContentCards(
      continueSearches,
      logEvent,
      screenWidth,
      handleRecentSearchPress,
      appConfig.env,
    );
  }, [
    continueSearches,
    logEvent,
    screenWidth,
    handleRecentSearchPress,
    appConfig.env,
  ]);

  useEffect(() => {
    if (!hasTrackedRef.current && contentCardProps.length > 0) {
      const items = contentCardProps.map(
        (item: HorizontalContentCardProps) => ({
          item_id: item.contentDetailsPropsValues?.primaryText ?? '',
          item_name: '',
          item_category: item.contentDetailsPropsValues?.secondaryText ?? '',
        }),
      );

      const eventData = Platform.select({
        default: {
          category: TRACKING_CONSTANTS.RECENT_SEARCH_CATEGORY,
          lineofbusiness: TRACKING_CONSTANTS.LINE_OF_BUSINESS,
          screenName: TRACKING_CONSTANTS.SCREEN_NAME,
          currency: TRACKING_CONSTANTS.CURRENCY,
          items,
        },
      });

      if (eventData) {
        logEvent(TRACKING_CONSTANTS.ACTIONS.VIEW_ITEM_LIST, {
          param: [eventData],
        });
        hasTrackedRef.current = true;
      }

      // Fire last searched viewed event for all displayed recent searches
      if ((continueSearches as HotelRecentSearch[]).length > 0) {
        const destinations = (continueSearches as HotelRecentSearch[]).map(
          (search, index) => ({
            destinationId: search.destination?.id || '',
            position: index,
          }),
        );
        const analyticsParams = SearchWidgetEvent.lastSearchedViewedEvent({
          destinations,
        });
        logEvent(EVENT_NAMES.LAST_SEARCHED_VIEWED, analyticsParams);
      }
    }
  }, [contentCardProps, continueSearches, logEvent, EVENT_NAMES]);

  // Loading state
  if (isLoading) {
    return null;
  }

  // Empty state
  if (!contentCardProps.length) {
    return null;
  }
  return (
    <View style={styles.recentContainer} testID={testID}>
      {showTitle && (
        <Text
          style={[
            getTextStyle(
              pd.sys.typescale.body_text_medium,
              pd.sys.colour.content_primary,
            ),
            {
              marginBottom: pd.ref.space.spacing20.px,
            },
          ]}
        >
          {t('searchHotelPage.recentSearches.title')}
        </Text>
      )}

      <View style={styles.cardContainer}>
        <HorizontalContentCardGroupView
          orientation={Constants.CAROUSEL_ORIENTATION}
          contentCardProps={contentCardProps}
          viewHeight={104}
          flatListProps={
            {
              showsHorizontalScrollIndicator: false,
              testID: 'recent-search-flatlist',
            } as FlatListProps<HorizontalContentCardProps>
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    // Align the cards without shifting the native scroll content outside its bounds.
    marginLeft: -Constants.CAROUSEL_ORIENTATION.horizontalSpacing,
    overflow: 'hidden',
    // Offset the unused space below the cards in the fixed-height carousel.
    marginBottom: -pd.ref.space.spacing30.px,
  },
  recentContainer: {
    marginTop: pd.ref.space.spacing30.px,
    paddingLeft: pd.ref.space.spacing40.px,
  },
});

export default RecentSearchView;
